import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Phone from 'components/icons/phone';
import { Customer } from 'data/firestore/agent/customer/types';
import { EntityGet } from 'data/firestore/types';
import getDisplayName from 'data/services/profile/getDisplayName';
import getInitials from 'data/services/string/getInitials';
import { Phonenumber } from 'data/firestore/agent/customer/phonenumber/types';
import useUserRoles from 'hooks/useUserRoles';
import useCallCustomer from 'hooks/useCallCustomer';
import {
    Container, CustomerInfoContainer, NameLabel,
    StyledAvatar, StyledCallNowButton, StyledUniversalForm,
} from './styled';
import Fields from './fields';
import { FormValues } from './types';
import CustomerPhonePicker from './CustomerPhonePicker';

interface CustomerCallModalProps {
    customer: EntityGet<Customer>;
    phoneNumber?: Phonenumber;
    onRequestClose: () => void;
}

const ModalBody = ({
    customer,
    phoneNumber: customerFallbackNumber,
    onRequestClose,
}: CustomerCallModalProps) => {
    const { agentId = '' } = useParams();
    const { user } = useUserRoles();
    const {
        id, photoURL, phoneNumber: customerPhoneNumber,
    } = customer;
    const displayName = getDisplayName(customer);

    const [callNumber, setCallNumber] = useState(
        customerFallbackNumber?.phoneNumber || customerPhoneNumber,
    );

    const { callCustomer } = useCallCustomer({
        agentId,
        customerId: id,
    });

    const initials = useMemo(() => {
        if (displayName === 'Unknown') return null;
        return getInitials(displayName);
    }, [displayName]);

    const handleCallNow = async (values: FormValues) => {
        const {
            callScript,
            voicemailScript: voiceMailResponderId,
            phoneNumber,
        } = values;
        const responderId = callScript?.type === 'persona' ? callScript.id : undefined;
        const workflowId = callScript?.type === 'workflow' ? callScript.id : undefined;
        await callCustomer({
            responderId,
            phoneNumber,
            voiceMailResponderId,
            workflowId,
            customerPhoneNumber: callNumber as string,
        });
        onRequestClose();
    };

    return (
        <Container>
            <CustomerInfoContainer>
                <StyledAvatar
                    src={photoURL}
                    assistiveText={displayName}
                    initials={initials}
                    title={displayName}
                    value={id}
                />
                <NameLabel>
                    {displayName}
                </NameLabel>
                <CustomerPhonePicker
                    agentId={agentId}
                    customerId={id}
                    callNumber={callNumber}
                    onSwitchPhone={setCallNumber}
                />
            </CustomerInfoContainer>
            <StyledUniversalForm id="call-customer-form" initialValues={{ caller: user?.id }} onSubmit={handleCallNow}>
                <Fields agentId={agentId} />
            </StyledUniversalForm>
            <StyledCallNowButton
                type="submit"
                form="call-customer-form"
                variant="brand"
                size="medium"
                borderRadius="semi-rounded"
            >
                <Phone className="rainbow-m-right_x-small" />
                Call Now
            </StyledCallNowButton>
        </Container>
    );
};

export default ModalBody;
